import dayjs, { Dayjs, ManipulateType } from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { BELGIAN_TIMEZONE, DAY_OF_WEEK, GRANULARITY_DURATION, HOUR_LABEL_FORMAT } from 'constants/datetime.ts'
import { ConvertToFullPeriod } from 'types/date.ts'
import { Granularity } from 'types/contracts.ts'

dayjs.extend(isSameOrBefore)
dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Expresses the given moment in the Belgian timezone, so the period it belongs to no longer depends on the timezone of
 * the device. The API returns its datetimes as UTC instants, which resolve to the previous month, day or hour on every
 * device west of Belgium (e.g. the 1st of November at midnight is 2025-10-31T23:00:00.000Z)
 *
 * @param {dayjs.ConfigType} date
 * @returns {Dayjs}
 */
export const toBelgianTime = (date?: dayjs.ConfigType): Dayjs => dayjs(date).tz(BELGIAN_TIMEZONE)

/**
 * Reads a datetime without a timezone (e.g. the from & until of an API request) as a Belgian wall clock time
 *
 * @param {string} date
 * @returns {Dayjs}
 */
export const parseBelgianTime = (date: string): Dayjs => dayjs.tz(date, BELGIAN_TIMEZONE)

/**
 * Adds the given amount of units to a Belgian wall clock time, resolving the timezone offset again so a step over a DST
 * transition keeps its wall clock (e.g. midnight stays midnight instead of turning into 01:00 or 23:00)
 *
 * @param {dayjs.ConfigType} date
 * @param {number} amount
 * @param {dayjs.ManipulateType} unit
 * @returns {Dayjs}
 */
export const addBelgianTime = (date: dayjs.ConfigType, amount: number, unit: ManipulateType): Dayjs =>
  toBelgianTime(date).add(amount, unit).tz(BELGIAN_TIMEZONE, true)

/**
 * Returns an array of formatted dates between two given dates, looped by the given unit in the Belgian timezone
 *
 * @param {dayjs.ConfigType} startDate
 * @param {dayjs.ConfigType} endDate
 * @param {dayjs.ManipulateType} unit
 * @param {number} amount - The amount of units to step with, e.g. 15 minutes (defaults to 1)
 * @returns {string[]}
 */
export const getArrayOfFormattedDatesBetweenDates = (
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
  unit: ManipulateType,
  amount: number = 1
): string[] => {
  const results: string[] = []
  const start = toBelgianTime(startDate)
  const end = toBelgianTime(endDate)
  const step = amount || 1

  if (!start.isValid() || !end.isValid() || step < 0) return results

  // Compare instants instead of counting steps with diff: a diff across a DST change is off by the offset difference,
  // which added the period after endDate (e.g. November for an October endDate)
  for (let i = 0, date = start; !date.isAfter(end); i++, date = addBelgianTime(start, i * step, unit)) {
    results.unshift(date.toISOString())
  }

  return results
}

/**
 * Returns the start of the period the given date falls in, e.g. 08:15 for 08:20 with a QUARTER_HOUR granularity
 *
 * @param {dayjs.ConfigType} date
 * @param {Granularity} granularity
 * @returns {Dayjs}
 */
export const startOfGranularityPeriod = (date: dayjs.ConfigType, granularity: Granularity): Dayjs => {
  const { amount, unit } = GRANULARITY_DURATION[granularity]
  const startOfUnit = toBelgianTime(date).startOf(unit)

  // Periods spanning multiple units (e.g. 15 minutes) are floored to the closest multiple
  return amount === 1 ? startOfUnit : startOfUnit.set(unit, Math.floor(startOfUnit.get(unit) / amount) * amount)
}

/**
 * Checks whether the given granularity splits a single day into multiple periods, e.g. HOUR & QUARTER_HOUR
 *
 * @param {Granularity} granularity
 * @returns {boolean}
 */
export const isIntradayGranularity = (granularity: Granularity): boolean => {
  const { unit } = GRANULARITY_DURATION[granularity]

  return unit === 'hour' || unit === 'minute'
}

/**
 * Converts the given data to a full period (from - until) with the given granularity, filling every period without data
 * with the entry returned by createEmptyEntry. Entries outside the period are kept, so periods that do not align with
 * the granularity (e.g. a billing cycle) keep their trailing partial period.
 *
 * @template T
 * @param {ConvertToFullPeriod<T>} params
 * @returns {T[]}
 */
export const convertToFullPeriod = <T>({ data, from, until, granularity, getDate, createEmptyEntry }: ConvertToFullPeriod<T>): T[] => {
  const { amount, unit } = GRANULARITY_DURATION[granularity]

  // Parse every date once: the period is the gap-detection key, the timestamp is the sort key
  const entries = data.map((entry) => {
    const date = getDate(entry)
    return { entry, period: startOfGranularityPeriod(date, granularity).valueOf(), timestamp: dayjs(date).valueOf() }
  })
  const existingPeriods = new Set(entries.map(({ period }) => period))

  // Add an empty entry for every period between from & until that has no data
  for (const date of getArrayOfFormattedDatesBetweenDates(from, until, unit, amount)) {
    const period = startOfGranularityPeriod(date, granularity).valueOf()

    if (!existingPeriods.has(period)) {
      existingPeriods.add(period)
      entries.push({ entry: createEmptyEntry(date), period, timestamp: dayjs(date).valueOf() })
    }
  }

  // Sort chronologically
  return entries.sort((a, b) => a.timestamp - b.timestamp).map(({ entry }) => entry)
}

/**
 * Returns the formatted date as HH:mm
 *
 * @param {dayjs.ConfigType} date
 * @returns {string}
 */
export const formatHour = (date: dayjs.ConfigType): string => toBelgianTime(date).format(HOUR_LABEL_FORMAT)

/**
 * Check if a given date is a weekend (Saturday or Sunday) in Belgium, as the opening hours it guards are Belgian
 *
 * @param {dayjs.ConfigType} d
 * @returns {boolean}
 */
export const isWeekend = (d?: dayjs.ConfigType): boolean => {
  const date = d ? toBelgianTime(d) : toBelgianTime()
  return date.day() === DAY_OF_WEEK.saturday || date.day() === DAY_OF_WEEK.sunday
}
